import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { ItemThumb } from '../world/ItemSprite';
import { itemPath } from '../../utils/worldFormat';
import { itemRequirementText } from '../../utils/classBuilds';
import { resolveSkillLink, METHOD_LABEL } from '../../utils/skillLinks';
import type { SkillLink } from '../../utils/skillLinks';
import type { ClassSkill } from '../../types/classBuild';
import type { CharacterClass } from '../../types/progression';
import type { WorldData } from '../../types/world';
const btn = 'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-lg px-2 py-1.5 text-xs font-semibold text-[#D4AF37] transition-colors duration-150 hover:bg-[#D4AF37]/10';

/**
 * Habilidad → ítem que la enseña (o equipo/mascota que la da) → su ficha con "Dónde conseguirlo".
 * La relación sale del registro central por skill_id e item_id; cada dato lleva su propio estado.
 */
export function ClassSkills({
  cls,
  skills,
  w,
  loading
}: {
  cls: CharacterClass;
  skills: ClassSkill[];
  w?: WorldData;
  loading: boolean;
}) {
  return <ul className="divide-y divide-white/10 border-y border-white/10" data-id="element-340" data-mp="1scjho8i94km9">
      {skills.map(s => {
      const link = w ? resolveSkillLink(w, cls, s.name) : undefined;
      return <li key={s.name} className="grid gap-2 py-3 md:grid-cols-[13rem_1fr] md:gap-4" data-id="element-341" data-mp="1jblettjjuj59">
            <div data-id="element-342" data-mp="1j9s688pdloum">
              <p className="font-semibold text-white" data-id="element-343" data-mp="2g1m0vksjaliv">{s.name}</p>
              {link?.def && <p className="text-xs text-gray-500" data-id="element-344" data-mp="2b2rgwfityqx6">skill_id {link.def.skillId}</p>}
              {s.note && <p className="text-xs text-gray-400" data-id="element-345" data-mp="2b0y9gy0e82o4">{s.note}</p>}
            </div>
            <div className="min-w-0" data-id="element-346" data-mp="26zcqcux9ms5h">{loading || !link ? <p className="text-sm text-gray-500" data-id="element-347" data-mp="222m7y0xznkgw">Cargando datos del servidor…</p> : <SkillLinkView link={link} cls={cls} data-id="element-348" data-mp="224ffrjkc8aqh" />}</div>
          </li>;
    })}
    </ul>;
}
function SkillLinkView({
  link,
  cls
}: {
  link: SkillLink;
  cls: CharacterClass;
}) {
  if (link.learn.length) {
    return <div className="space-y-2" data-id="element-349" data-mp="s3l6w0t4ijv2">
        <ul className="space-y-2" data-id="element-350" data-mp="s5eblak6eu0f">
          {link.learn.map(ci => <li key={ci.id} className="flex items-center gap-3" data-id="element-351" data-mp="x3fwm115x3ej">
              <ItemThumb id={ci.id} name={ci.name} size="thumb" placeholder data-id="element-352" data-mp="j3ewinsq2004" />
              <span className="min-w-0 flex-1" data-id="element-353" data-mp="j351glstl2nk">
                <span className="block text-[11px] uppercase tracking-wide text-gray-500" data-id="element-354" data-mp="n4gul1awk60i">Ítem para aprender la habilidad</span>
                <Link to={itemPath(ci.id)} className="block truncate text-sm font-semibold text-white hover:text-[#D4AF37]" data-id="element-355" data-mp="9ctroimgfz8r">
                  {ci.name}
                </Link>
                <span className="block text-xs text-gray-400" data-id="element-356" data-mp="3vll72tsftky4">
                  item_id {ci.id} · Requisito del ítem: {itemRequirementText(ci.item)}
                </span>
              </span>
              <Link to={`${itemPath(ci.id)}#donde-conseguirlo`} className={btn} data-id="element-357" data-mp="3mjlr84t5smwr">
                Dónde conseguirlo <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" data-id="element-358" data-mp="3rjhshwah208m" />
              </Link>
            </li>)}
        </ul>
        <Status link={link} data-id="element-359" data-mp="37xycx09n0clt" />
      </div>;
  }
  if (link.origin.length) {
    const what = link.status === 'mascota' ? 'una mascota' : 'equipo';
    return <div className="space-y-2" data-id="element-360" data-mp="3gymjxb0m5cp7">
        <p className="text-sm text-gray-300" data-id="element-361" data-mp="3gzo3k3vp37ij">
          No se aprende con un pergamino: la da {what} con skill_id {link.def?.skillId} compatible con {cls.base}.
        </p>
        <ul className="flex flex-wrap gap-2" data-id="element-362" data-mp="3qviv5ohwccxg">
          {link.origin.map(ci => <li key={ci.id} data-id="element-363" data-mp="3lxppcwki9kxp">
              <Link to={itemPath(ci.id)} className="inline-flex items-center gap-2 rounded-xl border border-white/10 py-1 pl-1 pr-3 text-sm text-gray-200 transition-colors duration-150 hover:border-[#D4AF37]/40" data-id="element-364" data-mp="3lweksuj8k95j">
                <ItemThumb id={ci.id} name={ci.name} size="thumb" focusable={false} placeholder data-id="element-365" data-mp="3vnsprhtyi162" />
                {ci.name}
              </Link>
            </li>)}
        </ul>
        {link.originTotal > link.origin.length && <p className="text-xs text-gray-500" data-id="element-366" data-mp="8ggnspnzy2y9">Y {link.originTotal - link.origin.length} más con el mismo poder en “Equipo compatible”.</p>}
        <Status link={link} data-id="element-367" data-mp="27wt86lt1w2e1" />
      </div>;
  }
  return <div className="space-y-1" data-id="element-368" data-mp="27v9v142oy7gh">
      <p className="text-sm text-gray-400" data-id="element-369" data-mp="2co2v3en7srm8">Relación pendiente de documentar: ningún ítem, arma ni mascota compatible con {cls.base} tiene este poder en la exportación.</p>
      {link.problems.map(p => <p key={p} className="text-xs text-amber-300/90" data-id="element-370" data-mp="1k0p7w89bjafb">
          {p}
        </p>)}
    </div>;
}
function Status({
  link
}: {
  link: SkillLink;
}) {
  return <div className="space-y-0.5" data-id="element-371" data-mp="1t0lpjpxaj90v">
      {link.method && <p className="text-xs text-gray-500" data-id="element-372" data-mp="1syshq7axyira">{METHOD_LABEL[link.method]}</p>}
      {link.status === 'sin-imagen' && <p className="text-xs text-gray-500" data-id="element-373" data-mp="1y0q8dmlo8fu9">Este ítem todavía no tiene imagen asignada.</p>}
      {link.problems.map(p => <p key={p} className="text-xs text-amber-300/90" data-id="element-374" data-mp="34hvakgw2i94a">
          Por revisar: {p}
        </p>)}
    </div>;
}